import React, { useEffect, useState } from "react";
import { Trophy, Sparkles } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ReferralLeaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/leaderboard/referrals?limit=50").then((r) => setRows(r.data)).catch(() => setRows([]));
    if (user) {
      api.get("/me/referrals").then((r) => setMe(r.data)).catch(() => setMe(null));
    }
  }, [user]);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto" data-testid="referral-leaderboard-page">
        <div className="flex items-center gap-3 mb-2">
          <Trophy size={28} className="text-[#FFD60A]" />
          <h1 className="font-heading text-3xl sm:text-4xl font-black">Friends-brought leaderboard</h1>
        </div>
        <p className="text-zinc-400 mt-2">Every time someone joins via your shared link, you earn a point. Bring more friends, climb the board.</p>

        {me && (
          <div className="mt-6 pp-card-elevated p-4 flex items-center justify-between gap-3" data-testid="my-stats-banner">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-full bg-[#00E676]/15 border border-[#00E676]/30 text-[#00E676] flex items-center justify-center"><Sparkles size={20} /></span>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Your score</div>
                <div className="font-heading text-2xl font-black mt-0.5">{me.total} friends brought</div>
                {me.rank && <div className="text-xs text-zinc-400 mt-0.5">Rank #{me.rank}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 pp-card overflow-hidden">
          <table className="w-full text-sm" data-testid="referral-leaderboard-table">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-white/5">
              <tr>
                <th className="text-start px-4 py-3 w-16">Rank</th>
                <th className="text-start px-4 py-3">Player</th>
                <th className="text-end px-4 py-3 w-32">Friends</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user.id} className={`border-t border-white/5 ${user && row.user.id === user.id ? "bg-[#00E676]/5" : ""}`} data-testid={`referral-row-${row.rank}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold ${row.rank === 1 ? "bg-[#FFD60A] text-black" : row.rank === 2 ? "bg-zinc-300 text-black" : row.rank === 3 ? "bg-[#cd7f32] text-black" : "bg-white/5 text-zinc-400"}`}>{row.rank}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8"><AvatarImage src={row.user.avatar_url || ""} /><AvatarFallback className="bg-[#007AFF] text-white text-xs">{(row.user.name || "?").charAt(0)}</AvatarFallback></Avatar>
                      <div>
                        <div className="font-medium">{row.user.name}{user && row.user.id === user.id && <span className="ms-2 pp-chip text-[10px] text-[#00E676] border-[#00E676]/40">You</span>}</div>
                        <div className="text-[11px] text-zinc-500 uppercase">{row.user.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <span className="font-bold text-[#00E676]">{row.score}</span>
                    <Sparkles size={11} className="inline -mt-0.5 ms-1 text-[#00E676]" />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} className="text-center text-zinc-500 py-12">No referrals yet — be the first to bring a friend.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
